import { useEffect, useMemo, useRef, useState } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { useCollectionArtists } from '../hooks/useCollectionArtists';

function classNames(...cls) { return cls.filter(Boolean).join(' '); }

function CopyButton({ text, label = 'Copy' }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => {
        navigator.clipboard.writeText(text || '');
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      }}
      className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-50"
    >
      {copied ? 'Copied' : label}
    </button>
  );
}

function ArtistCard({ a, collectionId }) {
  const name = a.nickname || [a.first_name, a.last_name].filter(Boolean).join(' ') || 'Unknown';
  const acceptanceUrl = a.acceptance?.acceptance_uuid
    ? `${window.location.origin}/acceptance/${a.acceptance.acceptance_uuid}`
    : null;
  return (
    <div className="rounded-lg border border-gray-200 p-3 flex flex-col gap-3 bg-white shadow-sm">
      <div className="flex items-start gap-3">
        <img
          src={a.profile_img || 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="96" height="96"><rect width="100%" height="100%" fill="%23f3f4f6"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-family="Arial" font-size="12" fill="%239ca3af">No Photo</text></svg>'}
          alt={name}
          className="w-12 h-12 rounded-full object-cover bg-gray-100"
          onError={(e) => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="96" height="96"><rect width="100%" height="100%" fill="%23f3f4f6"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-family="Arial" font-size="12" fill="%239ca3af">No Photo</text></svg>';
          }}
        />
        <div className="flex-1 min-w-0">
          <div className="font-medium truncate">{name}</div>
          <div className="text-xs text-gray-500 truncate">{a.email || 'No email'}</div>
          <div className="mt-1 flex flex-wrap gap-1">
            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
              Artworks: {a.artworks_total}
            </span>
            <span className={classNames(
              'text-xs px-2 py-0.5 rounded-full',
              a.accepted_count > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
            )}>
              Accepted: {a.accepted_count}
            </span>
            {a.has_app && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">Has App</span>
            )}
            {a.has_paid && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Paid</span>
            )}
            {a.has_submitted_artwork && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">Submitted</span>
            )}
          </div>
        </div>
        {a.email && <CopyButton text={a.email} label="Copy Email" />}
      </div>

      <div className="flex items-center justify-between gap-3">
        <div className="text-xs text-gray-500">Joined: {a.joined_collection ? new Date(a.joined_collection).toLocaleDateString() : '—'}</div>
        <div className="flex items-center gap-2">
          {acceptanceUrl ? (
            <>
              <span className={classNames(
                'text-xs px-2 py-0.5 rounded-full',
                a.acceptance?.accepted_at ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-800'
              )}>
                {a.acceptance?.accepted_at ? 'Terms accepted' : 'Terms pending'}
              </span>
              <CopyButton text={acceptanceUrl} label="Copy Terms URL" />
            </>
          ) : (
            <span className="text-xs text-gray-400">No terms link</span>
          )}
        </div>
      </div>

      {a.accepted_thumbs?.length > 0 && (
        <div className="flex -mx-0.5">
          {a.accepted_thumbs.map((t) => (
            <img
              key={t.vc_id}
              src={t.cover_image}
              alt={`Accepted ${t.vc_id}`}
              className="w-12 h-12 object-cover rounded m-0.5 border border-gray-200"
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function Artists() {
  const { collectionId } = useOutletContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') || '';
  const sort = searchParams.get('sort') || 'name'; // name | newest | accepted

  const { items, stats, loading, error } = useCollectionArtists(collectionId);

  const filtered = useMemo(() => {
    let list = items;
    if (q) {
      const s = q.toLowerCase();
      list = list.filter((a) =>
        (a.nickname && a.nickname.toLowerCase().includes(s)) ||
        ((a.first_name || '').toLowerCase().includes(s)) ||
        ((a.last_name || '').toLowerCase().includes(s)) ||
        ((a.email || '').toLowerCase().includes(s))
      );
    }
    switch (sort) {
      case 'newest':
        list = [...list].sort((a, b) => new Date(b.joined_collection || 0) - new Date(a.joined_collection || 0));
        break;
      case 'accepted':
        list = [...list].sort((a, b) => (b.accepted_count || 0) - (a.accepted_count || 0));
        break;
      default:
        list = [...list].sort((a, b) => {
          const an = (a.nickname || `${a.first_name || ''} ${a.last_name || ''}`).trim().toLowerCase();
          const bn = (b.nickname || `${b.first_name || ''} ${b.last_name || ''}`).trim().toLowerCase();
          return an.localeCompare(bn);
        });
    }
    return list;
  }, [items, q, sort]);

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="text-sm text-gray-600">Artists: <span className="font-medium text-gray-900">{stats.total}</span></div>
          <div className="text-sm text-gray-600">Accepted: <span className="font-medium text-gray-900">{stats.acceptedArtists}</span></div>
          <div className="text-sm text-gray-600">Terms: <span className="font-medium text-gray-900">{stats.termsAccepted}</span></div>
          <div className="text-sm text-gray-600">Has App: <span className="font-medium text-gray-900">{stats.withApp}</span></div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <input
              className="h-9 w-60 rounded border border-gray-300 px-3 text-sm"
              placeholder="Search artists…"
              value={q}
              onChange={(e) => setSearchParams((prev) => {
                const p = new URLSearchParams(prev);
                if (e.target.value) p.set('q', e.target.value); else p.delete('q');
                return p;
              })}
            />
          </div>
          <select
            className="h-9 rounded border border-gray-300 px-2 text-sm"
            value={sort}
            onChange={(e) => setSearchParams((prev) => { const p = new URLSearchParams(prev); p.set('sort', e.target.value); return p; })}
          >
            <option value="name">Sort: Name</option>
            <option value="newest">Sort: Newest</option>
            <option value="accepted">Sort: Most Accepted</option>
          </select>
        </div>
      </div>

      {loading && <div className="text-sm text-gray-500">Loading artists…</div>}
      {error && <div className="text-sm text-red-600">{String(error.message || error)}</div>}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {filtered.map((a) => (
          <ArtistCard key={a.uuid} a={a} collectionId={collectionId} />
        ))}
      </div>
      {filtered.length === 0 && !loading && !error && (
        <div className="text-sm text-gray-500">No artists found.</div>
      )}
    </div>
  );
}
